import { Action, Reducer } from 'redux';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/Person.Actions';

// models
import { Person } from '../../models/Entities/Person/Person';

// state
import { PersonState, PersonUnloadState } from '../states/PersonState';

export type KnownAction =
  | Actions.Persons.PersonGetAllRequestAction
  | Actions.Persons.PersonGetAllSuccessAction
  | Actions.Persons.PersonGetAllFailureAction
  | Actions.Persons.PersonGetbyIdRequestAction
  | Actions.Persons.PersonGetByIdSuccessAction
  | Actions.Persons.PersonGetByIdFailureAction
  | Actions.Persons.PersonCreateRequestAction
  | Actions.Persons.PersonCreateSuccessAction
  | Actions.Persons.PersonCreateFailureAction
  | Actions.Persons.PersonUpdateRequestAction
  | Actions.Persons.PersonUpdateSuccessAction
  | Actions.Persons.PersonUpdateFailureAction
  | Actions.Persons.PersonDeleteRequestAction
  | Actions.Persons.PersonDeleteSuccessAction
  | Actions.Persons.PersonDeleteFailureAction
  | Actions.Persons.PersonFixIdentifierRequestAction
  | Actions.Persons.PersonFixIdentifierSuccessAction
  | Actions.Persons.PersonFixIdentifierFailureAction;

export const PersonsReducer: Reducer<PersonState> = (
  state: PersonState | undefined,
  incomingAction: Action
): PersonState => {
  if (state === undefined) {
    return PersonUnloadState as PersonState;
  }

  const action = incomingAction as KnownAction;

  switch (action.type) {
    case ActionEvents.PERSON_GETALL_REQUEST:
      return {
        ...state,
        isLoadingAll: true,
        successLoadingAll: undefined,
        failOnLoadingAll: false,
        filter: action.filter,
        error: undefined
      };
    case ActionEvents.PERSON_GETALL_SUCCESS:
      return {
        ...state,
        isLoadingAll: false,
        successLoadingAll: true,
        failOnLoadingAll: false,
        list: action.payload,
        error: undefined
      };
    case ActionEvents.PERSON_GETALL_FAILURE:
      return {
        ...state,
        isLoadingAll: false,
        successLoadingAll: false,
        failOnLoadingAll: true,
        error: action.error
      };

    case ActionEvents.PERSON_GETBYID_REQUEST:
      return {
        ...state,
        isLoadingOne: true,
        successLoadingOne: undefined,
        failOnLoadingOne: false,
        error: undefined
      };
    case ActionEvents.PERSON_GETBYID_SUCCESS: {
      const others = state.list.filter((person: Person) => person.id !== action.payload.id);
      return {
        ...state,
        isLoadingOne: false,
        successLoadingOne: true,
        failOnLoadingOne: false,
        list: [...others, action.payload],
        error: undefined
      };
    }
    case ActionEvents.PERSON_GETBYID_FAILURE:
      return {
        ...state,
        isLoadingOne: false,
        successLoadingOne: false,
        failOnLoadingOne: true,
        error: action.error
      };

    case ActionEvents.PERSON_CREATE_REQUEST:
      return {
        ...state,
        isAddingNewOne: true,
        successAddingNewOne: undefined,
        failOnAddingNewOne: false,
        error: undefined
      };
    case ActionEvents.PERSON_CREATE_SUCCESS: {
      const createdList = state.list.some((person: Person) => person.id === action.payload.id)
        ? state.list.map((person: Person) => (person.id === action.payload.id ? action.payload : person))
        : [...state.list, action.payload];
      return {
        ...state,
        isAddingNewOne: false,
        successAddingNewOne: true,
        failOnAddingNewOne: false,
        lastCreated: action.payload,
        list: createdList,
        error: undefined
      };
    }
    case ActionEvents.PERSON_CREATE_FAILURE:
      return {
        ...state,
        isAddingNewOne: false,
        successAddingNewOne: false,
        failOnAddingNewOne: true,
        error: action.error
      };

    case ActionEvents.PERSON_UPDATE_REQUEST:
      return {
        ...state,
        isUpdatingOne: true,
        successUpdatingOne: undefined,
        failOnUpdatingOne: false,
        list: state.list.map((person: Person) =>
          person.id === action.payload.id
            ? { ...person, isUpdating: true, isUpdatedSuccessfully: undefined, failOnUpdating: false, error: undefined }
            : person
        ),
        error: undefined
      };
    case ActionEvents.PERSON_UPDATE_SUCCESS: {
      const updatedList = state.list.some((person: Person) => person.id === action.payload.id)
        ? state.list.map((person: Person) =>
            person.id === action.payload.id ? { ...person, ...action.payload, isUpdating: false, isUpdatedSuccessfully: true } : person
          )
        : [...state.list, action.payload];
      return {
        ...state,
        isUpdatingOne: false,
        successUpdatingOne: true,
        failOnUpdatingOne: false,
        list: updatedList,
        error: undefined
      };
    }
    case ActionEvents.PERSON_UPDATE_FAILURE:
      return {
        ...state,
        isUpdatingOne: false,
        successUpdatingOne: false,
        failOnUpdatingOne: true,
        list: state.list.map((person: Person) =>
          person.id === action.personId
            ? { ...person, isUpdating: false, isUpdatedSuccessfully: false, failOnUpdating: true, error: action.error }
            : person
        ),
        error: action.error
      };

    case ActionEvents.PERSON_DELETE_REQUEST:
      return {
        ...state,
        isDeletingOne: true,
        successDeletingOne: undefined,
        failOnDeletingOne: false,
        list: state.list.map((person: Person) =>
          person.id === action.payload
            ? { ...person, isDeleting: true, isDeletedSuccessfully: undefined, failOnDeleting: false, error: undefined }
            : person
        ),
        error: undefined
      };
    case ActionEvents.PERSON_DELETE_SUCCESS:
      return {
        ...state,
        isDeletingOne: false,
        successDeletingOne: true,
        failOnDeletingOne: false,
        list: state.list.filter((person: Person) => person.id !== action.payload),
        error: undefined
      };
    case ActionEvents.PERSON_DELETE_FAILURE:
      return {
        ...state,
        isDeletingOne: false,
        successDeletingOne: false,
        failOnDeletingOne: true,
        list: state.list.map((person: Person) =>
          person.id === action.personId
            ? { ...person, isDeleting: false, isDeletedSuccessfully: false, failOnDeleting: true, error: action.error }
            : person
        ),
        error: action.error
      };

    case ActionEvents.PERSON_FIX_IDENTIFIER_REQUEST:
      return {
        ...state,
        isFixingIdentifiers: true,
        successFixingIdentifiers: undefined,
        failOnFixingIdentifiers: false,
        error: undefined
      };
    case ActionEvents.PERSON_FIX_IDENTIFIER_SUCCESS:
      return {
        ...state,
        isFixingIdentifiers: false,
        successFixingIdentifiers: true,
        failOnFixingIdentifiers: false,
        error: undefined
      };
    case ActionEvents.PERSON_FIX_IDENTIFIER_FAILURE:
      return {
        ...state,
        isFixingIdentifiers: false,
        successFixingIdentifiers: false,
        failOnFixingIdentifiers: true,
        error: action.error
      };

    default:
      return state;
  }
};
